import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Build as BuildIcon,
  Assignment as AssignmentIcon,
  History as HistoryIcon,
  Add as AddIcon,
  Logout as LogoutIcon,
  Dashboard as DashboardIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { useAuth } from "../../context/AuthContext";
import { maintenanceAPI } from "../../utils/api";
import Layout from "../common/Layout";
import MaintenanceRequestForm from "./MaintenanceRequestForm";
import UserMaintenanceRequests from "./UserMaintenanceRequests";

const UserDashboard = () => {
  const { userProfile, logout } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    assigned: 0,
    inProgress: 0,
    resolved: 0,
    rejected: 0,
    closed: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await maintenanceAPI.getStats();
      setStats(response.data);
      setError("");
    } catch (error) {
      console.error("Error fetching maintenance stats:", error);
      setError("Failed to load maintenance statistics");
    } finally {
      setLoading(false);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  };

  return (
    <Layout title="User Dashboard">
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h4" gutterBottom>
              Maintenance Portal
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              {getGreeting()}, {userProfile?.name}! Submit and track your
              maintenance requests.
            </Typography>
          </Box>
          <Tooltip title="Logout">
            <IconButton
              onClick={logout}
              color="error"
              sx={{
                border: 1,
                borderColor: "error.main",
                "&:hover": {
                  backgroundColor: "error.light",
                  color: "white",
                },
              }}
            >
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Quick Stats */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <DashboardIcon color="info" sx={{ mr: 1 }} />
                  <Typography variant="h6">Total</Typography>
                </Box>
                <Typography variant="h3" color="info.main">
                  {loading ? <CircularProgress size={20} /> : stats.total}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  All requests
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <AssignmentIcon color="warning" sx={{ mr: 1 }} />
                  <Typography variant="h6">Pending</Typography>
                </Box>
                <Typography variant="h3" color="warning.main">
                  {loading ? <CircularProgress size={20} /> : stats.pending}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Awaiting assignment
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <AssignmentIcon color="info" sx={{ mr: 1 }} />
                  <Typography variant="h6">Assigned</Typography>
                </Box>
                <Typography variant="h3" color="info.main">
                  {loading ? <CircularProgress size={20} /> : stats.assigned}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Worker assigned
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <BuildIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">In Progress</Typography>
                </Box>
                <Typography variant="h3" color="primary.main">
                  {loading ? <CircularProgress size={20} /> : stats.inProgress}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Being worked on
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                  <Typography variant="h6">Resolved</Typography>
                </Box>
                <Typography variant="h3" color="success.main">
                  {loading ? <CircularProgress size={20} /> : stats.resolved}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Completed
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <CancelIcon color="error" sx={{ mr: 1 }} />
                  <Typography variant="h6">Rejected</Typography>
                </Box>
                <Typography variant="h3" color="error.main">
                  {loading ? <CircularProgress size={20} /> : stats.rejected}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Declined
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Main Content */}
        <Paper sx={{ p: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            sx={{ mb: 3 }}
            variant="fullWidth"
          >
            <Tab
              label="Submit Request"
              icon={<AddIcon />}
              iconPosition="start"
            />
            <Tab
              label="My Requests"
              icon={<AssignmentIcon />}
              iconPosition="start"
            />
            <Tab
              label="Request History"
              icon={<HistoryIcon />}
              iconPosition="start"
            />
          </Tabs>

          {activeTab === 0 && <MaintenanceRequestForm onSuccess={fetchStats} />}
          {activeTab === 1 && <UserMaintenanceRequests />}
          {activeTab === 2 && <UserMaintenanceRequests showHistory={true} />}
        </Paper>
      </Box>
    </Layout>
  );
};

export default UserDashboard;
